// Shared fetch layer for the learner-facing APIs.
//
// Why this exists: every api/*.js had its own copy of createSession /
// fetchWithSession with **no timeout and no error normalisation**. A request
// that hung (a slow LLM grade, a slow GitHub commit) hung the UI until the
// hosting gateway dropped the socket, at which point the browser raised a bare
// `TypeError: Failed to fetch` — which CohortPilotPage showed to the learner
// verbatim. That is the "Failed to fetch" on the Save to repo journal button:
// it is a dead socket, not an application error, so it carried no detail and
// nothing was logged.
//
// Two guarantees here:
//   1. Every request has a deadline, so it fails fast and predictably instead
//      of dying whenever some upstream proxy decides to cut it.
//   2. Every failure arrives as an Error a human can act on, tagged with
//      `kind` so callers can distinguish "the network died" (safe to retry)
//      from "the server said no" (retrying will not help).

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8050';

// Deadlines are per class of work, not one global number: a progress PUT that
// takes 20s is broken, whereas an LLM grade legitimately takes that long.
// Each must still be shorter than the hosting gateway's own cut-off, or the
// browser sees a dropped socket before our timeout ever fires.
export const TIMEOUTS = {
  quick: 15000,    // plain DB reads/writes: progress, clinic records, journal
  upload: 45000,   // file upload to the repo
  llm: 60000,      // semantic grading — backend allows up to 45s of model time
  github: 60000,   // commit + push to the cohort repo
};

export class ApiError extends Error {
  constructor(message, { kind = 'server', status = 0, cause = null } = {}) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;       // 'network' | 'timeout' | 'server' | 'offline'
    this.status = status;
    this.cause = cause;
    this.retryable = kind !== 'server';
  }
}

const NETWORK_MESSAGE =
  'Could not reach the server. Your work has not been lost — check your connection and try again.';

const TIMEOUT_MESSAGE =
  'The server took too long to respond. Your work has not been lost — try again in a moment.';

const OFFLINE_MESSAGE =
  'You appear to be offline. Your work has not been lost — reconnect and try again.';

const withTimeout = async (url, options, ms) => {
  // AbortSignal.timeout() is not in every browser the cohort uses (older
  // Safari on the MacBooks), so drive an AbortController manually.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    return await fetch(url, { ...options, credentials: 'include', signal: controller.signal });
  } catch (error) {
    if (controller.signal.aborted) {
      throw new ApiError(TIMEOUT_MESSAGE, { kind: 'timeout', cause: error });
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      throw new ApiError(OFFLINE_MESSAGE, { kind: 'offline', cause: error });
    }
    // This is where a bare `TypeError: Failed to fetch` used to escape to the UI.
    throw new ApiError(NETWORK_MESSAGE, { kind: 'network', cause: error });
  } finally {
    clearTimeout(timer);
  }
};

const createSession = async (timeout) => {
  const response = await withTimeout(`${API_BASE_URL}/api/v1/sessions`, { method: 'POST' }, timeout);
  if (!response.ok) {
    throw new ApiError('Unable to create learner session', { kind: 'server', status: response.status });
  }
  return response.json();
};

// Sends the request, refreshing an expired session once on a 401.
export const fetchWithSession = async (url, options = {}, timeout = TIMEOUTS.quick) => {
  let response = await withTimeout(url, options, timeout);
  if (response.status !== 401) return response;
  await createSession(TIMEOUTS.quick);
  response = await withTimeout(url, options, timeout);
  return response;
};

// Sends the request and returns parsed JSON, raising a typed ApiError otherwise.
export const requestJson = async (url, { timeout = TIMEOUTS.quick, fallbackMessage = 'Request failed', ...options } = {}) => {
  const response = await fetchWithSession(url, options, timeout);
  if (response.status === 204) return null;
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiError(payload.detail || fallbackMessage, { kind: 'server', status: response.status });
  }
  return payload;
};
